/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState } from 'react';
import { Box, HStack, VStack, Table } from 'chakra-v3';
import { Text } from '../../components/Text';
import { CgArrowsExpandRight } from 'react-icons/cg';
import PipelineModal from './PipelineModal';
import { FeatureCard } from './FeatureCard';

type PipelineStep = {
  number: string;
  title: string;
  bgColor: '#f3fcf8' | '#f9fcf3';
  expandedContent: React.ReactNode;
  hasLearnMoreButton?: boolean;
};

interface PipelineCardProps {
  number: string;
  title: string;
  bgColor: '#f3fcf8' | '#f9fcf3';
  onReadMoreClick: () => void;
}

const PipelineCard: React.FC<PipelineCardProps> = ({ number, title, bgColor, onReadMoreClick }) => {
  return (
    <Box
      bg={bgColor}
      border="1px solid"
      borderColor="rgba(10, 49, 60, 0.1)"
      borderRadius="4px"
      p={5}
      w={{ base: '100%', md: '220px' }}
      flexShrink={0}
    >
      <VStack gap="27px" alignItems="flex-start" w="100%">
        {/* Number and Line */}
        <VStack gap={2} alignItems="flex-start" w="100%">
          <Text
            fontFamily="heading"
            fontSize="14px"
            fontWeight="normal"
            lineHeight="normal"
            letterSpacing="-1px"
            color="#0a313c"
            w="100%"
          >
            {number}
          </Text>
          <Box h="1px" w="100%" bg="rgba(10, 49, 60, 0.2)" />
        </VStack>

        {/* Title */}
        <Text
          fontFamily="heading"
          fontSize={{ base: '18px', sm: '20px', md: '24px', lg: '24px' }}
          fontWeight="normal"
          lineHeight="28.8px"
          letterSpacing="-1px"
          color="#0a313c"
          h={{ base: 'auto', md: '180px', lg: '180px' }}
          w={{ base: '100%', md: '160px', lg: '160px' }}
          whiteSpace="pre-wrap"
        >
          {title}
        </Text>

        {/* Read more link */}
        <HStack
          as="button"
          gap={3}
          alignItems="center"
          p={2}
          bg="transparent"
          border="none"
          cursor="pointer"
          onClick={onReadMoreClick}
          _hover={{ opacity: 0.8 }}
        >
          <Text fontFamily="body" fontSize="16px" fontWeight="normal" lineHeight="normal" color="#0a313c">
            Read more
          </Text>
          <Box display="flex" alignItems="center" justifyContent="center" w="20px" h="20px">
            <CgArrowsExpandRight size={20} color="#0a313c" />
          </Box>
        </HStack>
      </VStack>
    </Box>
  );
};

const Methodology: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const featureCards = [
    {
      title: 'Offers a clear, comparative view',
      description:
        'of how top AI models actually perform when tested against Indian languages, contexts, and everyday needs.',
    },
    {
      title: 'Reveals the gaps',
      description:
        'from cultural misfires to unsafe advice—that current models routinely overlook, and outlines where they need to improve',
    },
    {
      title: 'Sets out practical guardrails and best practices',
      description: 'for deploying sovereign AI safely in high-impact public and population scale settings.',
    },
    {
      title: 'Creates meaningful earning and learning opportunities',
      description:
        'for Karya workers, who contribute directly to the evaluation through paid microtasks and skill-building in the emerging AI assessment economy.',
    },
  ];

  const pipelineCards: PipelineStep[] = [
    {
      number: '01',
      title: 'Domain expert consultation',
      bgColor: '#f3fcf8' as const,
      expandedContent: (
        <>
          We identify 2–4 expert orgs in each domain of interest and conduct focused interviews to understand:
          <ul style={{ marginTop: '0', marginBottom: '0', paddingLeft: '24px', listStyleType: 'disc' }}>
            <li style={{ marginBottom: '0' }}>the kinds of queries users might pose to chatbots and</li>
            <li>what safe and useful responses should — and should not — look like</li>
          </ul>
          <br />
          We synthesize insights from multiple interviews into:
          <ul style={{ marginTop: '0', paddingLeft: '24px', listStyleType: 'disc' }}>
            <li style={{ marginBottom: '0' }}>the kinds of queries users might pose to chatbots and</li>
            <li>what safe and useful responses should — and should not — look like</li>
          </ul>
        </>
      ),
      hasLearnMoreButton: false,
    },
    {
      number: '02',
      title: 'Benchmark dataset development',
      bgColor: '#f9fcf3' as const,
      expandedContent: (
        <>
          We design a query-creation microtask and a query-validation microtask on the Karya app. Workers generate a
          small set of queries for a given theme. Validators proofread them, ensuring they are on-theme, contain
          necessary context, avoid personally identifiable information, and differ meaningfully from one another.
          <br />
          <br />
          We recruit data workers across all languages of interest, ensuring balanced representation across gender,
          educational background, and pin code.
          <br />
          <br />
          We develop and deliver training modules introducing workers to the goals of the evaluation and preparing them
          to create realistic queries grounded in expert-derived themes and their own concerns.
          <br />
          <br />
          Query creation and validation are completed as paid microtasks on the Karya app, with validators providing
          real-time feedback. The resulting dataset is reviewed by Karya&apos;s internal team of language specialists.
          <br />
          <br />
          The final benchmark dataset contains over 500 queries per domain and language — over 22,000 queries in total.
          <br />
          <br />
          To prevent benchmark leakage or model contamination, this dataset is kept private until evaluation is
          complete. Every query is posed to all 5 models, and all responses are collected.
        </>
      ),
      hasLearnMoreButton: false,
    },
    {
      number: '03',
      title: 'Small-scale review of AI responses by domain specialists',
      bgColor: '#f3fcf8' as const,
      expandedContent: (
        <>
          For each domain, we select a small number of challenging queries for specialist review and invite previously
          interviewed experts to a co-evaluation workshop facilitated by an HCI researcher.
          <br />
          <br />
          Their representatives collectively review the prompts and model responses, discussing factual accuracy, local
          appropriateness, and safety or legal risks.
          <br />
          <br />
          The workshop is loosely structured, enabling essential checks while allowing specialists to raise new concerns
          and explore emerging issues.
        </>
      ),
      hasLearnMoreButton: true,
    },
    {
      number: '04',
      title: 'Large-scale evaluation of AI responses by end user subset',
      bgColor: '#f9fcf3' as const,
      expandedContent: (
        <>
          We design check-the-answer and compare-the-answer microtasks on the Karya app.
          <ul style={{ marginTop: '0', marginBottom: '0', paddingLeft: '24px', listStyleType: 'disc' }}>
            <li style={{ marginBottom: '0' }}>In the first, a worker evaluates a chatbot response.</li>
            <li>In the second, a worker chooses the better of two responses and explains their choice.</li>
          </ul>
          <br />
          To ensure these tasks gather calibrated and meaningful input, they are refined via a pilot study. After the
          pilot, we select four evaluation criteria for end user review: language quality, content quality,
          trustworthiness, and local relevance.
          <br />
          <br />
          <ul style={{ marginTop: '0', marginBottom: '0', paddingLeft: '24px', listStyleType: 'disc' }}>
            <li style={{ marginBottom: '0' }}>
              We evaluate 14 conversational AI models on a sample of queries from every domain and language, generating
              over 1,50,000 microtasks.
            </li>
          </ul>
          <br />
          <ul style={{ marginTop: '0', paddingLeft: '24px', listStyleType: 'disc' }}>
            <li>
              We recruit a diverse and balanced cohort of data workers, introduce them to the project goals, and provide
              detailed training. Evaluation proceeds as paid microtasks on the Karya app.
            </li>
          </ul>
        </>
      ),
      hasLearnMoreButton: false,
    },
    {
      number: '05',
      title: 'Massive-scale expansion using LLM-as-a-judge',
      bgColor: '#f3fcf8' as const,
      expandedContent: (
        <>
          <Text
            as="span"
            fontFamily="body"
            fontSize="16px"
            fontWeight="500"
            fontStyle="italic"
            color="#0a313c"
            lineHeight="normal"
          >
            Evaluating all 17 models on all 22,000+ queries manually would require over 1 million microtasks, which is
            possible, but time-consuming and at a high cost.
          </Text>
          <br />
          <br />
          Instead, after the large-scale but manageable manual phase, we extend evaluation automatically across all
          models and queries. We use an ensemble of four LLM-as-a-judge models to evaluate all candidate systems across
          the full set of 22,000+ queries.
          <br />
          <br />
          To improve alignment with human perspectives, we conduct additional experiments in which LLM judges are
          primed with selected human evaluation data. This grounds automated assessment in a range of end-user
          viewpoints rather than abstract scoring criteria.
          <br />
          <br />
          This combined approach strengthens the relationship between automated and human evaluation, improving
          correlation by up to 80%. The result is a scalable method that preserves the insight of population-level
          review while enabling massive-scale assessment.
        </>
      ),
      hasLearnMoreButton: false,
    },
  ];

  const handleReadMoreClick = (index: number) => {
    setCurrentStepIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentStepIndex < pipelineCards.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const coreDimensionsData = [
    {
      dimension: 'Accuracy',
      description: 'Clinical correctness / factuality',
      derivedFrom: 'Expert insights',
    },
    {
      dimension: 'Cultural Fit',
      description: 'Sensitivity to local norms, language, practices',
      derivedFrom: 'Expert + Worker inputs',
    },
    {
      dimension: 'Safety and Trustworthiness',
      description: 'Risk of harmful / misleading advice',
      derivedFrom: 'Expert',
    },
    {
      dimension: 'Clarity',
      description: 'Understandability for non-expert users',
      derivedFrom: 'Worker evaluation',
    },
  ];


  return (
    <Box w="100%" bg="#F7F3EF" pt="80px" pb={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }}>
      <VStack
        gap={{ base: 8, md: 15 }}
        alignItems="flex-start"
        w="100%"
        maxW={{ base: '100%', md: '1200px' }}
        mx="auto"
      >
        {/* Main Heading */}
        <Text
          fontFamily="heading"
          fontSize={{ base: '32px', sm: '36px', md: '40px', lg: '72px' }}
          fontWeight="300"
          lineHeight="normal"
          letterSpacing="-1px"
          color="#0a313c"
          w="100%"
          whiteSpace="pre-wrap"
        >
          Methodology
        </Text>

        {/* Introductory Paragraph */}
        <Text
          fontFamily="body"
          fontSize="16px"
          fontWeight="normal"
          lineHeight="1.6"
          color="#1a1a1a"
          w="100%"
          whiteSpace="pre-wrap"
        >
          Samiksha focuses on using participatory methods to empower end users in AI development ensuring they are both
          creators and beneficiaries of these technologies. This makes Samiksha best suited for any model intended for
          use in India&apos;s growing AI environment.
        </Text>

        {/* Feature Overview Cards - Single column on mobile, 2x2 grid on md+ */}
        <Box
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          gap={6}
          w="100%"
          py={{ base: 8, md: 12 }}
        >
          {featureCards.map((card, index) => (
            <Box key={index} w="100%">
              <FeatureCard title={card.title} description={card.description} />
            </Box>
          ))}
        </Box>

        {/* Pipeline Instantiation Paragraph */}
        <Text
          fontFamily="body"
          fontSize="16px"
          fontWeight="normal"
          lineHeight="1.6"
          color="#1a1a1a"
          w="100%"
          whiteSpace="pre-wrap"
        >
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="normal" color="#1a1a1a">
            In this first instantiation of the Samiksha pipeline, we evaluate{' '}
          </Text>
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="700" color="#1a1a1a">
            17
          </Text>
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="700" color="#1a1a1a">
            {' conversational AI models'}{' '}
          </Text>
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="normal" color="#1a1a1a">
            on their ability to respond safely and usefully to user queries across{' '}
          </Text>
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="700" color="#006ca7">
            4 domains (healthcare, education, finance, legal)
          </Text>
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="normal" color="#1a1a1a">
            {' '}
            and{' '}
          </Text>
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="700" color="#006ca7">
            11 languages
          </Text>
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="600" color="#1a1a1a">
            {' '}
          </Text>
          <Text as="span" fontFamily="body" fontSize="16px" fontWeight="normal" color="#1a1a1a">
            (Assamese, Bengali, English, Gujarati, Hindi, Kannada, Malayalam, Marathi, Punjabi, Tamil, Telugu).
          </Text>
        </Text>

        {/* Pipeline Implementation Details */}
        <VStack gap="40px" alignItems="flex-start" w="100%" pt={{ base: 8, md: 12 }}>
          <Text
            fontFamily="body"
            fontSize="24px"
            fontWeight="normal"
            lineHeight="1.4"
            letterSpacing="0.12px"
            color="#0a313c"
            w="100%"
            whiteSpace="pre-wrap"
          >
            The details of pipeline implementation are as follows:
          </Text>

          <HStack
            gap="20px"
            alignItems="flex-start"
            flexWrap="wrap"
            w="100%"
            flexDirection={{ base: 'column', md: 'row' }}
          >
            {pipelineCards.map((card, index) => (
              <PipelineCard
                key={card.number}
                number={card.number}
                title={card.title}
                bgColor={card.bgColor}
                onReadMoreClick={() => handleReadMoreClick(index)}
              />
            ))}
          </HStack>
        </VStack>

        {/* Pipeline Modal */}
        {isModalOpen && (
          <PipelineModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            currentStep={
              pipelineCards[currentStepIndex] as {
                number: string;
                title: string;
                bgColor: '#f3fcf8' | '#f9fcf3';
                expandedContent: React.ReactNode;
                hasLearnMoreButton?: boolean;
              }
            }
            currentStepIndex={currentStepIndex}
            totalSteps={pipelineCards.length}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        )}

        {/* Core Dimensions Table */}
        <VStack gap={8} alignItems="flex-start" w="100%" pt={{ base: 8, md: 12 }} pb={{ base: 8, md: '70px' }}>
          <Text
            fontFamily="heading"
            fontSize={{ base: '24px', sm: '28px', md: '32px', lg: '40px' }}
            fontWeight="300"
            lineHeight="normal"
            letterSpacing="-1px"
            color="#0a313c"
            w="100%"
            textAlign="center"
            whiteSpace="pre-wrap"
          >
            Core Dimensions
          </Text>

          <Box
            w="100%"
            maxW="1069px"
            mx="auto"
            border="0.8px solid"
            borderColor="rgba(10, 49, 60, 0.25)"
            borderRadius="4px"
            overflowX="auto"
          >
            <Table.Root variant="outline" size="sm" w="100%">
              <Table.Header>
                <Table.Row bg="#166176">
                  <Table.ColumnHeader
                    fontFamily="body"
                    fontSize={{ base: '14px', md: '16px', lg: '16px' }}
                    fontWeight="700"
                    lineHeight="24px"
                    color="white"
                    p={{ base: 4, md: 6, lg: 6 }}
                  >
                    Dimension
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    fontFamily="body"
                    fontSize={{ base: '14px', md: '16px', lg: '16px' }}
                    fontWeight="700"
                    lineHeight="24px"
                    color="white"
                    p={{ base: 4, md: 6, lg: 6 }}
                  >
                    Description
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    fontFamily="body"
                    fontSize={{ base: '14px', md: '16px', lg: '16px' }}
                    fontWeight="700"
                    lineHeight="24px"
                    color="white"
                    p={{ base: 4, md: 6, lg: 6 }}
                  >
                    Derived From
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {coreDimensionsData.map((row, index) => (
                  <Table.Row
                    key={index}
                    bg="rgba(255, 255, 255, 0.5)"
                    borderBottom={index < coreDimensionsData.length - 1 ? '0.8px solid' : 'none'}
                    borderColor="rgba(10, 49, 60, 0.25)"
                  >
                    <Table.Cell
                      fontFamily="body"
                      fontSize={{ base: '13px', md: '15px', lg: '15px' }}
                      fontWeight="normal"
                      lineHeight="22.5px"
                      color="#0a313c"
                      p={{ base: 4, md: 6, lg: 6 }}
                    >
                      {row.dimension}
                    </Table.Cell>
                    <Table.Cell
                      fontFamily="body"
                      fontSize={{ base: '13px', md: '15px', lg: '15px' }}
                      fontWeight="normal"
                      lineHeight="22.5px"
                      color="#0a313c"
                      p={{ base: 4, md: 6, lg: 6 }}
                    >
                      {row.description}
                    </Table.Cell>
                    <Table.Cell
                      fontFamily="body"
                      fontSize={{ base: '13px', md: '15px', lg: '15px' }}
                      fontWeight="normal"
                      lineHeight="22.5px"
                      color="#0a313c"
                      p={{ base: 4, md: 6, lg: 6 }}
                    >
                      {row.derivedFrom}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </VStack>
      </VStack>
    </Box>
  );
};

export default Methodology;
